import * as Html from "../../util/Html.ts";
import * as modal from "../../util/modal.ts";
import * as amplify from "../../util/amplify.ts";
import * as FwDatas from "../../FwDatas.ts";
import ApiServer from "../../ApiServer.ts";
import * as actionMenu from "../action-menu-registry.ts"
import * as i18n from "../../i18n.ts"
import { TaskListCache } from "../../server/objects/TaskBeans.ts";
import { addSubmitHandler } from "../../util/forms.ts";
import * as segment from "../../tracking-segment.ts";
import { clampQuantity, formatUnitName, localUnit, rememberUnit, unitChoices } from "../../lib/unitFormat.ts";
import { isFeatureEnabled } from "../../lib/launchdarkly/launchdarkly.ts";
import { PIECE } from "../../lib/ingredientParser.ts";
import { itemName, listsRendered, revealItems, toastItemAdded } from "../../list/task_undo.ts";

Html.registerWidget('widget/form/taskedit',{initDom})


actionMenu.register({
  tasklistselect(){
    var
      menu = this,
      input = menu.querySelector('input'),
      selectedId = input.value

    return {menu:TaskListCache.datas.map(list=>{return {
      //svg:list.iconsimple,
      iconclass:"color-branded",
      title:list.name,
      value:list.metaId,
      selected:selectedId===list.metaId,
      clickFunction(){
        input.value = (list.metaId)
        input.dispatchEvent(new Event('change'))
      }
    }})};
  }
})


/** Value of the option opening the free unit input. Never submitted, the option unselects itself. */
const ADD_UNIT = '$addunit';

/**
 * Quantity + unit of a shopping list item. The unit select offers the same choices as the quantity
 * picker of the list, plus an entry to type one that is missing.
 */
function initQuantity(node :HTMLElement){
  const
    block = node.querySelector('.fw-quantity-block') as HTMLElement,
    quantity = node.querySelector('[name="quantity"]') as HTMLInputElement,
    select = node.querySelector('[name="unit"]') as HTMLSelectElement;

  //localUnit(): the chips speak the language, an item written by another client may carry a unitKey
  let current = localUnit(select.getAttribute('data-value')) || PIECE;

  function fill(){
    select.innerHTML = unitChoices(current).map(one=>
      `<option value="${Html.escape(one)}"${one===current?' selected':''}>${Html.escape(formatUnitName(one))}</option>`
    ).join('')+`<option value="${ADD_UNIT}">+ ${Html.escape(i18n.get('task_details.addunittitle'))}</option>`;
  }

  select.addEventListener('change',async ()=>{
    if(select.value!==ADD_UNIT){
      current = select.value;
      return;
    }
    const typed = (await modal.askText({
      title:'task_details.addunittitle',
      placeholder:'task_details.addunitplaceholder',
      buttonName:'actions.save',
      message:''
    }))?.trim();

    //a unit typed here is worth a chip in every picker, this item is not the only place it fits
    if(typed) current = rememberUnit(typed,i18n.getShortLanguage());
    //picked or cancelled, the "+" entry must not stay selected
    fill();
  })

  fill();

  return {
    /** A todo has no quantity: hidden fields are disabled so that they are not submitted either. */
    toggle(taskListType :string){
      const active = isFeatureEnabled('FWALL-33-list-item-quantity') && taskListType==='SHOPPING_LIST';
      block.style.display = active?'':'none';
      quantity.disabled = select.disabled = !active;
    },
    /** An empty or out-of-range field means no quantity, on the bounds the picker of the list uses. */
    normalize(){
      quantity.value = String(clampQuantity(quantity.valueAsNumber));
    }
  }
}

function initDom(node: HTMLElement){
  const metaId = node.getAttribute('data-fw-meta-id');
  const form = node as HTMLFormElement;

  const dueDate = node.querySelector('input[name="dueDate"]') as HTMLInputElement;
  const textInput = node.querySelector('input[name="text"]') as HTMLInputElement;
  textInput.focus()

  function hasDueDate(){
    return !!dueDate.value && dueDate.value !== '$empty';
  }
  //a new task can come with a due date already (quick add, calendar): it still gets the default reminder
  let hadDueDate = !!metaId && hasDueDate();

  function onDueDateChange(){
    const active = hasDueDate();
    const reminderBlock = node.querySelector('.fw-reminder-block') as HTMLElement;

    if(reminderBlock){
      reminderBlock.style.display = active ? '' : 'none';
      const reminderSelect = reminderBlock.querySelector('select') as HTMLSelectElement;
      if(reminderSelect){
        if(!active){
          reminderSelect.value = '';
          reminderSelect.dispatchEvent(new Event('change'));
        }else if(!hadDueDate && reminderSelect.value === ''){
          //a due date is meant to be reminded of, as on the apps: the calendar default reminder comes with it
          reminderSelect.dispatchEvent(new CustomEvent('reminderset',{
            detail:FwDatas.getSettingsPerFamily()?.defaultReminderAsString() || ''
          }));
        }
      }
    }
    hadDueDate = active;
  }

  const taskListIdSelect = node.querySelector("[name='taskListId']") as HTMLSelectElement;
  const taskCategorySelect = node.querySelector("[name='taskCategoryId']") as HTMLSelectElement;
  const quantityFields = initQuantity(node);
  let taskCategoryId = taskCategorySelect.getAttribute("data-value");

  function handleTaskList(){
    const taskList = TaskListCache.get(taskListIdSelect.value);
    quantityFields.toggle(taskList.taskListType);
    //use good category set
    if(taskCategorySelect.value){
      taskCategoryId = taskCategorySelect.value;
    }

    taskCategorySelect.innerHTML = '<option value="">🏷️ '+Html.escape(i18n.get('catsystem.uncategorized'))+'</option>'+taskList.getCategories(true).map(cat=>
      '<option value="'+cat.metaId+'" '+(taskCategoryId===cat.metaId?'selected':'')+'>'+Html.escape(cat.emoji+' '+cat.name)+'</option>'
    ).join('');

    //handle sharing of lits inside task
    if(taskList.sharedToAll){
      form.querySelectorAll<HTMLElement>('[data-accountid]').forEach(el=>el.style.display='')
    }else{
      form.querySelectorAll<HTMLElement>('[data-accountid]').forEach(el=>{
        const isSharedList = taskList.sharedMemberIds.indexOf(parseInt(el.getAttribute('data-accountid')))>-1
        el.style.display=isSharedList?'':'none'
        //we unclick those that are not sharable to
        if(!isSharedList && el.classList.contains('selected')){
          el.dispatchEvent(new Event("click"))
        }
      })
    }
  }

  taskListIdSelect.addEventListener('change', handleTaskList);
  dueDate.addEventListener('change', onDueDateChange);

  //we do it later to make sure it has been initialized
  setTimeout(function(){
    onDueDateChange();
    handleTaskList();
  },10);

  addSubmitHandler(form, async (form, event) => {
    quantityFields.normalize();
    const tracked = segment.formTaskTracker(form, metaId);
    if(metaId){
      await ApiServer.taskupdate2(form)
    }else{
      const created = await ApiServer.taskcreate2(form);
      toastItemAdded(itemName(created.text,created.quantity,created.unit),()=>ApiServer.metadelete([created.metaId]));
      listsRendered().then(()=>revealItems([created.metaId]));
    }
    tracked();
    modal.closeDomModal();
    amplify.publish('taskNeedRefresh');
    amplify.publish("activityUpdated");
  });
  
}